import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { errorHandler } from '../../../../services/util/error.service'
import PainelVeiculosEmRotaHttp from '../../../../services/api/https/dashboard/painel-veiculos-em-rota.http'
import { getPeriodoMesAtual, parseToMysqlFormat } from '../../../../services/util/date.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCHING_DATA: 'Buscando dados dos veículos em rota...',
  FETCHING_ENTREGAS: 'Buscando entregas do veículo...',
}

const INITIAL_STATE = {
  pInicial: getPeriodoMesAtual().dataInicial,
  pFinal: getPeriodoMesAtual().dataFinal,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,

  // Dados retornados da API
  entregas: null,
  entregasAtrasadas: null,
  total: null,
  veiculos: [],
  veiculosFiltrados: [],

  // Filtros
  searchText: '',
  ordenarPor: null,
  empresasSelecionadas: [],

  // Modal de detalhes do veículo
  modalVeiculo: null,
  entregasModal: [],

  // Auto refresh
  autoRefreshEnabled: true,

  // Restaurar modal após retorno da tela de canhoto
  pendingOpenFleetId: null,
}

const ORDENAR_OPTIONS = [
  { label: 'Por Data Envio - Crescente', value: 'data_envio_asc' },
  { label: 'Por Data Envio - Decrescente', value: 'data_envio_desc' },
  { label: 'Por % Status Final - Crescente', value: 'status_final_asc' },
  { label: 'Por % Status Final - Decrescente', value: 'status_final_desc' },
]

export default function usePainelVeiculosEmRota(selectedTp) {
  const [state, setState] = useState(INITIAL_STATE)
  const intervalRef = useRef(null)
  const prevSelectedTpRef = useRef(selectedTp)

  // Abre automaticamente o modal quando veículos carregam após restore de histórico
  useEffect(() => {
    if (!state.pendingOpenFleetId || state.veiculos.length === 0) return
    const veiculo = state.veiculos.find(v => v.fleet_id === state.pendingOpenFleetId)
    if (!veiculo) return
    setState(prev => ({
      ...prev,
      pendingOpenFleetId: null,
      modalVeiculo: veiculo,
      loadingMessage: LOAD_MESSAGE.FETCHING_ENTREGAS,
    }))
    PainelVeiculosEmRotaHttp.getEntregasVeiculo(veiculo.fleet_id, selectedTp)
      .then(entregas => {
        setState(prev => ({
          ...prev,
          entregasModal: entregas,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      })
      .catch(e => {
        setState(prev => ({
          ...prev,
          errorMessage: errorHandler(e),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      })
  }, [state.pendingOpenFleetId, state.veiculos, selectedTp])

  // Função de busca que será reutilizada
  const fetchData = useCallback(
    async (pInicial, pFinal) => {
      if (!selectedTp) {
        setState(prev => ({
          ...prev,
          errorMessage: 'Selecione um TP para buscar os dados',
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
        return
      }

      try {
        const data = await PainelVeiculosEmRotaHttp.getVeiculosEmRotaStats(
          selectedTp,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
        )

        setState(prev => ({
          ...prev,
          entregas: data.entregas,
          entregasAtrasadas: data.entregas_atrasadas,
          total: data.total,
          veiculos: data.veiculos,
          veiculosFiltrados: aplicarFiltrosEOrdenacao(
            data.veiculos,
            prev.searchText,
            prev.ordenarPor,
            prev.empresasSelecionadas,
          ),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      } catch (e) {
        setState(prev => ({
          ...prev,
          errorMessage: errorHandler(e),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      }
    },
    [selectedTp],
  )

  // Configura o auto refresh
  useEffect(() => {
    // Limpa intervalo anterior quando o componente desmonta ou selectedTp muda
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // Detecta mudança de TP e reseta os dados para fazer nova busca
  useEffect(() => {
    if (prevSelectedTpRef.current !== selectedTp) {
      prevSelectedTpRef.current = selectedTp
      setState(prev => ({
        ...prev,
        entregas: null,
        entregasAtrasadas: null,
        total: null,
        veiculos: [],
        veiculosFiltrados: [],
        modalVeiculo: null,
        entregasModal: [],
      }))
    }
  }, [selectedTp])

  // Busca automática ao entrar na tela se TP já está selecionado ou mudar de TP
  useEffect(() => {
    if (!selectedTp || state.entregas !== null) return
    setState(prev => ({
      ...prev,
      loadingMessage: LOAD_MESSAGE.FETCHING_DATA,
    }))
    fetchData(state.pInicial, state.pFinal)
  }, [selectedTp, fetchData, state.entregas])

  const localDispatch = useMemo(() => {
    return {
      resetState() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          successMessage: null,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onPeriodoInicialChange(value) {
        setState(prev => ({ ...prev, pInicial: value }))
      },

      onPeriodoFinalChange(value) {
        setState(prev => ({ ...prev, pFinal: value }))
      },

      async onBtnPesquisar(pInicial, pFinal) {
        if (!selectedTp) {
          setState(prev => ({
            ...prev,
            errorMessage: 'Selecione um TP para buscar os dados',
          }))
          return
        }

        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCHING_DATA }))
        await fetchData(pInicial, pFinal)

        // Configura auto refresh a cada 2 minutos
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }

        intervalRef.current = setInterval(
          () => {
            fetchData(pInicial, pFinal)
          },
          2 * 60 * 1000,
        ) // 2 minutos
      },

      stopAutoRefresh() {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      },

      onSearchTextChange(text) {
        setState(prev => {
          const veiculosFiltrados = aplicarFiltrosEOrdenacao(
            prev.veiculos,
            text,
            prev.ordenarPor,
            prev.empresasSelecionadas,
          )
          return { ...prev, searchText: text, veiculosFiltrados }
        })
      },

      onOrdenarPorChange(option) {
        setState(prev => {
          const veiculosFiltrados = aplicarFiltrosEOrdenacao(
            prev.veiculos,
            prev.searchText,
            option,
            prev.empresasSelecionadas,
          )
          return { ...prev, ordenarPor: option, veiculosFiltrados }
        })
      },

      onEmpresasSelecionadasChange(empresas) {
        setState(prev => {
          const veiculosFiltrados = aplicarFiltrosEOrdenacao(
            prev.veiculos,
            prev.searchText,
            prev.ordenarPor,
            empresas,
          )
          return { ...prev, empresasSelecionadas: empresas, veiculosFiltrados }
        })
      },

      onLimparFiltros() {
        setState(prev => {
          const veiculosFiltrados = aplicarFiltrosEOrdenacao(prev.veiculos, '', null, [])
          return {
            ...prev,
            searchText: '',
            ordenarPor: null,
            empresasSelecionadas: [],
            veiculosFiltrados,
          }
        })
      },

      async onVeiculoCardClick(veiculo) {
        setState(prev => ({
          ...prev,
          modalVeiculo: veiculo,
          loadingMessage: LOAD_MESSAGE.FETCHING_ENTREGAS,
        }))

        try {
          const entregas = await PainelVeiculosEmRotaHttp.getEntregasVeiculo(
            veiculo.fleet_id,
            selectedTp,
          )
          setState(prev => ({
            ...prev,
            entregasModal: entregas,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onModalClose() {
        setState(prev => ({
          ...prev,
          modalVeiculo: null,
          entregasModal: [],
        }))
      },

      async onRestoreFromHistory(fleet_id, pInicial, pFinal) {
        setState(prev => ({
          ...prev,
          pInicial,
          pFinal,
          pendingOpenFleetId: fleet_id,
          loadingMessage: LOAD_MESSAGE.FETCHING_DATA,
        }))
        await fetchData(pInicial, pFinal)
      },
    }
  }, [selectedTp, fetchData])

  return { state, localDispatch, ORDENAR_OPTIONS }
}

// Função auxiliar para aplicar filtros e ordenação
function aplicarFiltrosEOrdenacao(veiculos, searchText, ordenarPor, empresasSelecionadas) {
  let resultado = [...veiculos]

  // Aplica filtro de empresas
  if (empresasSelecionadas && empresasSelecionadas.length > 0) {
    const empresasSelecionadasNormalizadas = new Set(
      empresasSelecionadas
        .map(empresa => {
          if (typeof empresa === 'string') {
            return empresa
          }
          if (empresa && typeof empresa === 'object') {
            return empresa.value || empresa.label || empresa.name || ''
          }
          return ''
        })
        .map(empresa => String(empresa).trim().toLowerCase())
        .filter(Boolean),
    )

    resultado = resultado.filter(v => {
      return (
        Array.isArray(v.companies) &&
        v.companies.some(company =>
          empresasSelecionadasNormalizadas.has(
            String(company || '')
              .trim()
              .toLowerCase(),
          ),
        )
      )
    })
  }

  // Aplica filtro de texto
  if (searchText && searchText.trim()) {
    const textLower = searchText.toLowerCase().trim()
    resultado = resultado.filter(v => {
      const clientesMatch =
        Array.isArray(v.clients) &&
        v.clients.some(cliente => (cliente || '').toLowerCase().includes(textLower))

      return (
        v.vehicle_badge.toLowerCase().includes(textLower) ||
        v.driver_name.toLowerCase().includes(textLower) ||
        v.sent_time.toLowerCase().includes(textLower) ||
        String(v.fleet_id).includes(textLower) ||
        clientesMatch
      )
    })
  }

  // Aplica ordenação
  if (ordenarPor) {
    switch (ordenarPor.value) {
      case 'data_envio_asc':
        resultado.sort((a, b) => new Date(a.sent_time_raw) - new Date(b.sent_time_raw))
        break
      case 'data_envio_desc':
        resultado.sort((a, b) => new Date(b.sent_time_raw) - new Date(a.sent_time_raw))
        break
      case 'status_final_asc':
        resultado.sort((a, b) => a.percentual_status_final - b.percentual_status_final)
        break
      case 'status_final_desc':
        resultado.sort((a, b) => b.percentual_status_final - a.percentual_status_final)
        break
      default:
        break
    }
  }

  return resultado
}
